import {
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { CatalogItem } from "../types";
import { generateUniqueCatalogId } from "../utils/helpers";
import { RootState } from "./index";
import { deviceApi } from "../actions/devicesInQuery";
import { trapSiteApi } from "../actions/trapsiteInQuery";

const catalogItemsAdapter = createEntityAdapter<CatalogItem, string>({
  // Use our custom ID generator as the unique ID for the entity store
  selectId: (item) => generateUniqueCatalogId(item.source, item.id),
});

const catalogItemsSlice = createSlice({
  name: "catalogItems",
  initialState: catalogItemsAdapter.getInitialState(),
  reducers: {
    upsertCatalogItems: catalogItemsAdapter.upsertMany,
  },
});

export const { upsertCatalogItems } = catalogItemsSlice.actions;

const catalogItemsSelectors = catalogItemsAdapter.getSelectors<RootState>(
  (state) => state.catalogItems,
);

export const selectEnrichedCatalogItems = createSelector(
  [
    (state: RootState) => state.catalogSelection.selectedIds,
    (state: RootState) => state,
    deviceApi.endpoints.listDevices.select(),
    trapSiteApi.endpoints.listTrapSites.select(),
  ],
  (selectedIds, state, devicesResult, trapSitesResult): CatalogItem[] => {
    const devices = devicesResult.data?.items;
    const trapSites = trapSitesResult.data?.items;

    const deviceMap = new Map(devices?.map((d) => [d.uuid, d]));
    const siteMap = new Map(trapSites?.map((s) => [s._id, s]));

    return selectedIds
      .map((id) => {
        const item = catalogItemsSelectors.selectById(state, id);
        if (!item) return null;

        const device = deviceMap.get(item.device);
        const trapSite = siteMap.get(item.trap_site_id);

        const enriched: CatalogItem = {
          ...item,
          device_name: device?.display_name || "",
          trap_site_name: trapSite?.display_name || "",
        };
        return enriched;
      })
      .filter((item): item is CatalogItem => item !== null);
  },
);

export default catalogItemsSlice.reducer;
